// Angular
import { inject } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';

// Component Library
import {
  FsxOption,
  getFsxAutocompleteSelectedValue,
} from 'fsx-component-library';

// Local
import { addressFieldRequiredValidator } from '@app/validators/address-field-required.validator';
import { addressFieldsValidator } from '@app/validators/address-fields.validator';
import { AutocompleteValidator } from '@app/validators/autocomplete-validator';
import { noNumericsValidator } from '@app/validators/no-numerics-validator';
import { noSpecialCharsValidator } from '@app/validators/no-special-chars.validator';
import { zipCodeValidator } from '@app/validators/zip-code.validator';
import { Address } from '@data-contracts';

export interface IAddressFormGroup {
  addressType?: FormControl<string | FsxOption<number>>;
  primaryAddress: FormControl<boolean>;
  address1: FormControl<string>;
  address2: FormControl<string>;
  city: FormControl<string>;
  state: FormControl<string | FsxOption<string>>;
  zip: FormControl<string>;
}

export interface IRawAddressForm {
  primaryAddress: boolean;
  address1: string;
  address2: string;
  city: string;
  state: string | FsxOption<string>;
  zip: string;
  addressType?: string | FsxOption<number>;
}

export abstract class UsesAddressForm {
  /*********************************************************************************************************************
   * Service Injections
   ********************************************************************************************************************/
  protected readonly formBuilder = inject(FormBuilder);


  protected setAddressValidators(
    addressGroup: FormGroup<IAddressFormGroup>,
    isRequiredOverride: boolean,
    addressTypeRequired: boolean = true,
  ): void {
    const address1Control = addressGroup.controls.address1;
    const address2Control = addressGroup.controls.address2;
    const cityControl = addressGroup.controls.city;
    const stateControl = addressGroup.controls.state;
    const zipControl = addressGroup.controls.zip;
    const typeControl = addressGroup.controls.addressType;

    const isAddressRequired = isRequiredOverride
      ? [Validators.required]
      : [addressFieldRequiredValidator(addressGroup)];

    if (addressTypeRequired) {
      typeControl.setValidators([...isAddressRequired]);
    } else {
      typeControl.setValidators([]);
    }
    address1Control.setValidators([
      ...isAddressRequired,
      Validators.maxLength(100),
    ]);
    address2Control.setValidators([Validators.maxLength(100)]);
    cityControl.setValidators([
      ...isAddressRequired,
      Validators.maxLength(50),
      noNumericsValidator(),
      noSpecialCharsValidator(),
    ]);
    stateControl.setValidators([...isAddressRequired, AutocompleteValidator()]);
    zipControl.setValidators([
      ...isAddressRequired,
      Validators.maxLength(10),
      zipCodeValidator(),
    ]);
    addressGroup.setValidators([
      ...isAddressRequired,
      addressFieldsValidator(!addressTypeRequired),
    ]);

    // Update validity without marking the controls as touched
    addressGroup.updateValueAndValidity({ onlySelf: true });
    address1Control.updateValueAndValidity({ onlySelf: true });
    address2Control.updateValueAndValidity({ onlySelf: true });
    cityControl.updateValueAndValidity({ onlySelf: true });
    stateControl.updateValueAndValidity({ onlySelf: true });
    zipControl.updateValueAndValidity({ onlySelf: true });
  }

  protected generateAddressFormGroup(
    isAddressRequired: boolean = false,
    shouldBePrimary: boolean = false,
    requireAddressType: boolean = true,
  ): FormGroup<IAddressFormGroup> {
    const addressGroup = this.formBuilder.group<IAddressFormGroup>(
      {
        addressType: new FormControl<string | FsxOption<number>>(''),
        primaryAddress: new FormControl<boolean>(shouldBePrimary),
        address1: new FormControl<string>(''),
        address2: new FormControl<string>(''),
        city: new FormControl<string>(''),
        state: new FormControl<string | FsxOption<string>>(''),
        zip: new FormControl<string>(''),
      },
      { validators: addressFieldsValidator(!requireAddressType) },
    );
    this.setAddressValidators(addressGroup, isAddressRequired, requireAddressType);
    return addressGroup;
  }

  protected setExistingAddressValues(
    addressGroup: FormGroup<IAddressFormGroup>,
    existingValues: Address,
  ): void {
    addressGroup.patchValue({
      primaryAddress: existingValues.isPrimary,
      address1: existingValues.streetLine1,
      address2: existingValues.streetLine2,
      city: existingValues.city,
      zip:
        existingValues.zipCode +
        (existingValues.zipCodeExtension
          ? '-' + existingValues.zipCodeExtension
          : ''),
    });

    if (existingValues.state && existingValues.stateCode) {
      addressGroup.patchValue({
        state: {
          label: existingValues.state,
          value: existingValues.stateCode,
        } as FsxOption<string>,
      });
    }
    if (existingValues.type && existingValues.typeId) {
      addressGroup.patchValue({
        addressType: {
          label: existingValues.type,
          value: existingValues.typeId,
        } as FsxOption<number>,
      });
    }
  }

  protected mapFormGroupToAddress(
    formGroup: FormGroup<IAddressFormGroup>,
  ): Address {
    const formValue = formGroup.getRawValue();
    return this.mapFormGroupRawToAddress(formValue);
  }

  protected mapFormGroupRawToAddress(formValue: IRawAddressForm): Address {
    return {
      isPrimary: formValue.primaryAddress,
      streetLine1: formValue.address1,
      streetLine2: formValue.address2,
      city: formValue.city,
      state:
        typeof formValue.state === 'object'
          ? formValue.state.label
          : formValue.state,
      stateCode: getFsxAutocompleteSelectedValue(formValue.state),
      zipCode: formValue.zip?.substring(0, 5),
      zipCodeExtension:
        formValue.zip.length > 5
          ? (formValue.zip?.substring(
              formValue.zip.length - 4,
              formValue.zip.length,
            ) ?? null)
          : null,
      type:
        typeof formValue.addressType === 'object'
          ? formValue.addressType.label
          : formValue.addressType,
      typeId: getFsxAutocompleteSelectedValue(formValue.addressType),
    };
  }

  protected isAddressFormGroupEmpty(
    addressGroup: FormGroup<IAddressFormGroup>,
  ): boolean {
    // Intentionally checks only the user-facing fields, not addressType: addressType is a
    // hidden, system-managed default for single-address courts, so "the user entered an
    // address" is determined by the visible fields alone. Keeping the hidden type in sync
    // (cleared when these are empty) is handled in AddressFormService.
    const formValue = addressGroup.getRawValue();
    return (
      !formValue.address1 &&
      !formValue.address2 &&
      !formValue.city &&
      !formValue.state &&
      !formValue.zip
    );
  }
}
